import React, { useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from "recharts";
import {
  useTheme,
  Typography,
  useMediaQuery,
  Box,
  Paper,
  Stack,
  Chip,
  IconButton,
} from "@mui/material";
import WavesIcon from "@mui/icons-material/Waves";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useUnits } from "../../contexts/UnitContext"; // Ensure correct path

export const TideGraphExpanded = ({
  stationName,
  preparedData,
  extremePoints,
  tideStatus,
  currentHeight,
  closestPoint,
  dateRange,
  toggleExpanded,
}) => {
  const theme = useTheme();
  const { unitSystem, convert } = useUnits();
  const lastFormattedDateRef = useRef(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const chartDimensions = (() => {
    if (isMobile) {
      return {
        height: 250, // Taller for better mobile viewing
        minWidth: "100%", // Full width on mobile
        margin: { top: 10, right: 5, left: 5, bottom: 30 },
        fontSize: { tick: 10, label: 11 },
      };
    } else if (isTablet) {
      return {
        height: 280,
        minWidth: "100%",
        margin: { top: 10, right: 15, left: 10, bottom: 25 },
        fontSize: { tick: 11, label: 12 },
      };
    } else {
      return {
        height: 300,
        minWidth: "450px",
        margin: { top: 5, right: 20, left: 0, bottom: 20 },
        fontSize: { tick: 12, label: 12 },
      };
    }
  })();

  const convertHeight = (height) => {
    if (unitSystem === "metric") {
      return (height * 0.3048).toFixed(2);
    }
    return height.toFixed(2);
  };

  const getUnitLabel = () => {
    return unitSystem === "metric" ? "m" : "ft";
  };

  const formatTime = (time) => {
    if (!time) return "";
    return convert.time.format(time);
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const time = formatTime(new Date(label));
      const height = convertHeight(payload[0].value);

      return (
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 1 : 1.5,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            maxWidth: isMobile ? "160px" : "200px",
            fontSize: isMobile ? "0.75rem" : "0.875rem",
          }}
        >
          <Typography
            variant={isMobile ? "caption" : "subtitle2"}
            color="primary"
            sx={{ fontWeight: "bold" }}
          >
            {time}
          </Typography>
          <Typography variant={isMobile ? "caption" : "body2"}>
            Height: {height} {getUnitLabel()}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const CustomizedDot = (props) => {
    const { cx, cy, payload } = props;
    const currentPayloadTime = payload.t;

    // Current time dot
    if (closestPoint && currentPayloadTime === closestPoint.t.getTime()) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={isMobile ? 5 : 4}
          fill={theme.palette.secondary.main}
        />
      );
    }

    // Extreme points (just dots in expanded mode to avoid clutter)
    const extremePoint = extremePoints.find((p) => p.t === currentPayloadTime);
    if (extremePoint) {
      let color;
      if (extremePoint.type === "high") color = theme.palette.success.main;
      else if (extremePoint.type === "low") color = theme.palette.error.main;
      else if (extremePoint.type === "end") color = theme.palette.info.main;
      return <circle cx={cx} cy={cy} r={isMobile ? 4 : 3} fill={color} />;
    }
    return null;
  };

  const xAxisTickFormatter = (tick) => {
    const currentDate = new Date(tick);
    if (
      lastFormattedDateRef.current === null ||
      currentDate.toDateString() !== lastFormattedDateRef.current.toDateString()
    ) {
      lastFormattedDateRef.current = currentDate;
      return formatDateForDisplay(currentDate);
    }
    return "";
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: isMobile ? 1 : 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        width: "100%",
        minWidth: chartDimensions.minWidth,
        mb: 2,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <WavesIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
          <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary">
            Tide Graph
          </Typography>
        </Stack>

        <IconButton onClick={toggleExpanded} size="small">
          <CompareArrowsIcon fontSize="small" />
        </IconButton>
      </Stack>

      {stationName?.name && (
        <Typography
          variant={isMobile ? "caption" : "subtitle2"}
          color="textSecondary"
          sx={{ mb: 1, fontSize: isMobile ? "0.75rem" : "0.875rem" }}
        >
          {stationName.name}
        </Typography>
      )}

      {dateRange && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: "block",
            mt: -0.5,
            mb: 1,
            fontSize: isMobile ? "0.7rem" : "0.75rem",
          }}
        >
          {dateRange}
        </Typography>
      )}

      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 1 : 2}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        sx={{ mb: 2 }}
      >
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1}
          alignItems={isMobile ? "flex-start" : "center"}
        >
          <Chip
            label={`Tide: ${tideStatus}`}
            color="primary"
            size="small"
            sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
          />
          {currentHeight && (
            <Chip
              label={`Current: ${convertHeight(
                currentHeight
              )} ${getUnitLabel()}`}
              variant="outlined"
              size="small"
              sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
            />
          )}
        </Stack>
      </Stack>

      <Box sx={{ height: chartDimensions.height, width: "100%" }}>
        <ResponsiveContainer>
          <LineChart
            data={preparedData.map((item) => ({
              ...item,
              v: parseFloat(convertHeight(item.v)),
            }))}
            margin={chartDimensions.margin}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
            />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={xAxisTickFormatter}
              tick={{ fontSize: chartDimensions.fontSize?.tick || 12 }}
              stroke={theme.palette.text.secondary}
              dy={10}
              interval={isMobile ? "preserveStartEnd" : "auto"}
            />
            <YAxis
              tick={{ fontSize: chartDimensions.fontSize?.tick || 12 }}
              stroke={theme.palette.text.secondary}
              width={isMobile ? 35 : 60}
              label={{
                value: `Height (${getUnitLabel()})`,
                angle: -90,
                position: "insideLeft",
                style: {
                  textAnchor: "middle",
                  fill: theme.palette.text.secondary,
                  fontSize: chartDimensions.fontSize?.label || 12,
                },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="v"
              stroke={theme.palette.primary.main}
              strokeWidth={isMobile ? 1.5 : 2}
              dot={<CustomizedDot />} // Use CustomizedDot for current/extreme points
              activeDot={{ r: isMobile ? 4 : 5, strokeWidth: 1 }}
            />

            <ReferenceLine
              x={new Date().getTime()}
              stroke={theme.palette.secondary.main}
              strokeDasharray="3 3"
              label={{
                value: "Now",
                position: "insideTopRight",
                fill: theme.palette.secondary.main,
                fontSize: isMobile ? 9 : 10,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};
